import React, { useState, useEffect, useCallback } from 'react';
import { Footprints, Truck, PartyPopper, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import api, { formatApiErrorDetail } from '../lib/api';
import { toast } from 'sonner';

export const ParadeSignupCard = () => {
  const [signups, setSignups] = useState([]);
  const [role, setRole] = useState('walker');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/parade/signups').then((res) => setSignups(res.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const mine = signups.find((s) => s.is_me);

  const handleSignup = async () => {
    setSaving(true);
    try {
      await api.post('/parade/signup', { role, note });
      toast.success("You're signed up for the Christmas parade!");
      setNote('');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      await api.delete('/parade/signup');
      toast.success('Your parade signup was withdrawn.');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const walkers = signups.filter((s) => s.role === 'walker');
  const floatHelpers = signups.filter((s) => s.role === 'float');

  return (
    <Card className="bg-white border border-[#bd8cae]/40 shadow-md" data-testid="parade-signup-card">
      <CardHeader>
        <CardTitle className="font-serif text-xl text-[#3c4a2a] flex items-center gap-2">
          <PartyPopper className="w-5 h-5 text-[#a3688f]" />
          Lincolnton Christmas Parade — Nov 29
        </CardTitle>
        <p className="text-sm text-[#3c4a2a]/60">
          We're marching in the 68th Annual Lincolnton Christmas Parade (4:00–5:00 PM, Historic Downtown). Sign up to walk with us or help with the float!
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {mine ? (
          <div className="flex items-center justify-between bg-[#bd8cae]/15 rounded-lg px-4 py-3" data-testid="parade-my-signup">
            <p className="text-sm text-[#3c4a2a]">
              You're signed up as{' '}
              <span className="font-semibold">{mine.role === 'walker' ? 'a parade walker' : 'a float helper'}</span>. See you there! 🎄
            </p>
            <Button size="sm" variant="ghost" onClick={handleWithdraw} data-testid="parade-withdraw-btn" className="text-[#a3688f] hover:text-[#8a567a]">
              <X className="w-4 h-4 mr-1" /> Withdraw
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRole('walker')}
                data-testid="parade-role-walker"
                className={`flex-1 rounded-lg border px-3 py-2.5 text-sm flex items-center justify-center gap-2 transition-colors ${
                  role === 'walker' ? 'border-[#a3688f] bg-[#bd8cae]/20 text-[#a3688f] font-semibold' : 'border-[#bd8cae]/40 text-[#3c4a2a]/70 hover:bg-[#bd8cae]/10'
                }`}
              >
                <Footprints className="w-4 h-4" /> Walk in the Parade
              </button>
              <button
                type="button"
                onClick={() => setRole('float')}
                data-testid="parade-role-float"
                className={`flex-1 rounded-lg border px-3 py-2.5 text-sm flex items-center justify-center gap-2 transition-colors ${
                  role === 'float' ? 'border-[#a3688f] bg-[#bd8cae]/20 text-[#a3688f] font-semibold' : 'border-[#bd8cae]/40 text-[#3c4a2a]/70 hover:bg-[#bd8cae]/10'
                }`}
              >
                <Truck className="w-4 h-4" /> Help with the Float
              </button>
            </div>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note (e.g., bringing my daughter, can help decorate)"
              data-testid="parade-note-input"
            />
            <Button
              onClick={handleSignup}
              disabled={saving}
              data-testid="parade-signup-btn"
              className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
            >
              {saving ? 'Signing Up…' : 'Count Me In!'}
            </Button>
          </div>
        )}

        {signups.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="parade-signup-list">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#a3688f] font-semibold mb-2 flex items-center gap-1">
                <Footprints className="w-3.5 h-3.5" /> Walkers ({walkers.length})
              </p>
              <div className="space-y-1.5">
                {walkers.map((s, i) => (
                  <div key={`w-${i}`} className="bg-[#f1e3eb]/60 rounded px-3 py-1.5 text-sm text-[#3c4a2a]">
                    {s.name} {s.is_me && <Badge variant="secondary" className="bg-[#bd8cae]/30 text-[#a3688f] ml-1 text-[10px]">You</Badge>}
                    {s.note && <p className="text-xs text-[#3c4a2a]/55">{s.note}</p>}
                  </div>
                ))}
                {!walkers.length && <p className="text-xs text-[#3c4a2a]/45">No walkers yet — be the first!</p>}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#a3688f] font-semibold mb-2 flex items-center gap-1">
                <Truck className="w-3.5 h-3.5" /> Float Helpers ({floatHelpers.length})
              </p>
              <div className="space-y-1.5">
                {floatHelpers.map((s, i) => (
                  <div key={`f-${i}`} className="bg-[#f1e3eb]/60 rounded px-3 py-1.5 text-sm text-[#3c4a2a]">
                    {s.name} {s.is_me && <Badge variant="secondary" className="bg-[#bd8cae]/30 text-[#a3688f] ml-1 text-[10px]">You</Badge>}
                    {s.note && <p className="text-xs text-[#3c4a2a]/55">{s.note}</p>}
                  </div>
                ))}
                {!floatHelpers.length && <p className="text-xs text-[#3c4a2a]/45">No float helpers yet.</p>}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
